import { formatCurrency } from './format-currency.js';
import { formatMinorUnitsWithSeparators } from './format-minor-units-with-separators.js';
function markBalance(marking, isCredit, magnitude) {
    if (isCredit)
        return { text: `${magnitude} Cr`, isCredit: true };
    return { text: marking === 'both-sides' ? `${magnitude} Dr` : magnitude, isCredit: false };
}
/**
 * Renders a running, opening, closing or net balance accounting-style: a
 * CREDIT (negative) balance shows its magnitude with a trailing `Cr`, and a
 * DEBIT (positive) balance shows plainly.
 *
 * Why not a bare minus sign: on a statement of account the sign carries a
 * meaning a lay reader has to decode ("negative what?"), whereas `Cr` says it.
 * A client owed money should read `R129,637.50 Cr`, not `-R129,637.50`.
 *
 * The rule is **mark the exception, leave the expected direction plain**, and
 * which direction is expected depends on the ledger:
 *
 *  - On an AR statement, debit is the default reading (an amount the client
 *    owes), so `Dr` would add jargon without adding information. This is the
 *    `credit-only` default.
 *  - On a TRUST statement the default reading is the opposite: the firm
 *    normally HOLDS money for the client, which is a credit. A debit balance
 *    there is both the exception and a breach of LPC Rule 54.14.9, so leaving
 *    it unmarked lets the one figure that most needs to stand out read as
 *    ordinary. Those callers pass `both-sides`.
 *
 * Applies to BALANCE-style figures in LEDGER orientation only. Never use it on
 * a debit or credit COLUMN, which is already split by direction and therefore
 * carries unsigned magnitudes; and never on a figure that has been flipped out
 * of ledger orientation into plain English (a "funds held" total), because
 * `Cr`/`Dr` then describe the opposite of what the number means.
 */
export function formatSignedBalance(minorUnits, currency, marking = 'credit-only') {
    return markBalance(marking, minorUnits < 0, formatCurrency(Math.abs(minorUnits), currency));
}
/**
 * `formatSignedBalance` without the currency symbol, for a surface that states
 * its currency once in the heading block and leaves every cell bare - the
 * report and statement PDFs, whose amount columns use
 * `formatMinorUnitsWithSeparators` for exactly that reason.
 */
export function formatSignedBalanceWithoutSymbol(minorUnits, marking = 'credit-only') {
    return markBalance(marking, minorUnits < 0, formatMinorUnitsWithSeparators(Math.abs(minorUnits)));
}
